import React, { useEffect, useState } from 'react';
import { 
  Container, 
  Card, 
  CardContent, 
  Typography, 
  Box,
  AppBar,
  Toolbar,
  IconButton,
  FormControlLabel,
  Checkbox,
  Paper,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Chip,
  Divider
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import UtentiAttivi from './UtentiAttivi';
import { 
  Schedule, 
  ExitToApp,
  Store,
  CalendarMonth,
  AccessTime,
  ManageHistory,
  Person,
  PeopleAlt,
  GroupAdd,
  ChangeCircle,
  Download
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../api/authService';
import { dashboardService, dipendenteService, negozioService } from '../api/services';
import { Dipendente, Negozio, User } from '../types/types';
import MonthYearPicker from './MonthYearPicker';
import HiddenScheduleRenderer from './common/HiddenScheduleRenderer';
import { visibilitaService } from '../api/services';

type DashboardCard = {
  title: string;
  description: string;
  icon: React.ReactNode;
  path?: string;
  externalUrl?: string;
  onClick?: () => void;
  available: boolean;
  color: string;
  plain?: boolean; // se true, mostra solo l'icon (logo) senza titolo/descrizione e senza overlay colore
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const user: User | null = authService.getCurrentUser();

  // Impostazioni globali (backend API) - devono essere definite prima di qualsiasi return condizionale
  const [sliderAlwaysEnabled, setSliderAlwaysEnabled] = useState<boolean>(false);
  const [outboxEnabled, setOutboxEnabled] = useState<boolean>(true); // valore DB (toggle admin)
  const [outboxProfileFlag, setOutboxProfileFlag] = useState<boolean>(true); // flag profilo
  const [outboxEffective, setOutboxEffective] = useState<boolean>(true); // risultato finale
  const [loadingImpostazioni, setLoadingImpostazioni] = useState<boolean>(false);
  const [userLogValues, setUserLogValues] = useState<string[] | null>(null);
  const [loadingUserLog, setLoadingUserLog] = useState<boolean>(false);
  // Audit outbox (solo Admin)
  const [auditLoading, setAuditLoading] = useState<boolean>(false);
  const [auditSnackbar, setAuditSnackbar] = useState<{ open: boolean; severity: 'success' | 'error' | 'warning'; message: string }>({ open: false, severity: 'success', message: '' });
  const [logMeseLabel, setLogMeseLabel] = useState<string>('Febbraio');
  const [savingLogMese, setSavingLogMese] = useState<boolean>(false);
  // Eccezioni lock coordinatore (solo Admin)
  const [lockEccezioni, setLockEccezioni] = useState<any[]>([]);
  const [lockEccezioniLoading, setLockEccezioniLoading] = useState<boolean>(false);
  const [negozioDropdown, setNegozioDropdown] = useState<string>('');
  const [coordDropdown, setCoordDropdown] = useState<string>('');
  const [dipendenteNegozioDropdown, setDipendenteNegozioDropdown] = useState<string>('');
  const [dipendenteDropdown, setDipendenteDropdown] = useState<string>('');
  const [dipendenteOptions, setDipendenteOptions] = useState<Dipendente[]>([]);
  const [dipendentiLoading, setDipendentiLoading] = useState<boolean>(false);
  const [addingLockEcc, setAddingLockEcc] = useState<boolean>(false);
  const [negozioOptions, setNegozioOptions] = useState<Negozio[]>([]);
  
  // Stato per Scarica Orari Negozio (Dipendente)
  const [openMonthPicker, setOpenMonthPicker] = useState(false);
  const [downloadParams, setDownloadParams] = useState<{ negozio: string; mese: number; anno: number } | null>(null);
  
  // Stato per messaggio orari non pubblicati
  const [orariNonPubblicatiMsg, setOrariNonPubblicatiMsg] = useState<string | null>(null);

  // Stato per alert Differenza < 0 (Coordinatore)
  const [oreAlertOpen, setOreAlertOpen] = useState<boolean>(false);
  const [oreAlertNegozi, setOreAlertNegozi] = useState<{
    neg: string;
    negozio: string;
    dipendenti: { nome: string; cognome: string; differenza: number }[];
  }[]>([]);
  const [oreAlertMese, setOreAlertMese] = useState<string>('');

  // Stato per il selettore coordinatore (Admin)
  const [coordinatoriList, setCoordinatoriList] = useState<{ username: string; nome: string }[]>([]);
  const [selectedCoordinatore, setSelectedCoordinatore] = useState<string>('__tutti__');
  const [adminOreAlertLoading, setAdminOreAlertLoading] = useState<boolean>(false);
  const [oreAlertTriggeredByAdmin, setOreAlertTriggeredByAdmin] = useState<boolean>(false);
  // Dialog "Controllo Ore" Admin
  const [adminControlloDialogOpen, setAdminControlloDialogOpen] = useState<boolean>(false);
  const [adminSelectedMese, setAdminSelectedMese] = useState<number>(new Date().getMonth());
  const [adminSelectedAnno, setAdminSelectedAnno] = useState<number>(new Date().getFullYear());
  
  // Carica le impostazioni dal backend all'avvio
  useEffect(() => {
  const loadImpostazioni = async () => {
      try {
        const { impostazioniService } = await import('../api/impostazioniService');
        const impostazione = await impostazioniService.getTrascinaConfermareSempre();
        setSliderAlwaysEnabled(impostazione.enabled);
  const outbox = await impostazioniService.getOutboxEnabled();
  setOutboxEnabled(outbox.enabled);
  if (typeof outbox.profileFlag === 'boolean') setOutboxProfileFlag(outbox.profileFlag);
  if (typeof outbox.effectiveEnabled === 'boolean') setOutboxEffective(outbox.effectiveEnabled);
      } catch (error) {
        console.error('Errore nel caricamento delle impostazioni:', error);
        // Fallback al localStorage per compatibilità
        try {
          const raw = localStorage.getItem('appSettings');
          if (raw) {
            const parsed = JSON.parse(raw);
            setSliderAlwaysEnabled(!!parsed.sliderAlwaysEnabled);
      if (typeof parsed.outboxEnabled === 'boolean') setOutboxEnabled(parsed.outboxEnabled);
          }
        } catch {}
      }
    };
    
    if (user?.ruolo === 'Admin') {
      loadImpostazioni();
    }
  }, [user?.ruolo]);

  // Carica eccezioni lock coordinatore + lista negozi per i dropdown (solo Admin)
  useEffect(() => {
    const loadLockEccezioni = async () => {
      try {
        setLockEccezioniLoading(true);
        const [{ coordinatoreLockEccezioniService }, negozi] = await Promise.all([
          import('../api/impostazioniService'),
          negozioService.getAll()
        ]);
        const data = await coordinatoreLockEccezioniService.getAll();
        setLockEccezioni(data);
        setNegozioOptions(negozi);
      } catch (e) {
        console.error('Errore caricamento eccezioni lock:', e);
      } finally {
        setLockEccezioniLoading(false);
      }
    };
    if (user?.ruolo === 'Admin') loadLockEccezioni();
  }, [user?.ruolo]);

  const handleAddLockEccezione = async (tipo: 'NEGOZIO' | 'COORDINATORE' | 'DIPENDENTE', valore: string) => {
    if (!valore.trim()) return;
    try {
      setAddingLockEcc(true);
      const { coordinatoreLockEccezioniService } = await import('../api/impostazioniService');
      const dipendente = tipo === 'DIPENDENTE'
        ? dipendenteOptions.find(d => d.codicePersonale === valore.trim())
        : undefined;
      const nuova = await coordinatoreLockEccezioniService.create(
        tipo,
        valore.trim(),
        dipendente ? `${dipendenteNegozioDropdown} — ${dipendente.nome}` : '',
        tipo === 'DIPENDENTE' ? dipendenteNegozioDropdown : undefined
      );
      setLockEccezioni(prev => [...prev, nuova]);
      if (tipo === 'NEGOZIO') setNegozioDropdown('');
      else if (tipo === 'COORDINATORE') setCoordDropdown('');
      else setDipendenteDropdown('');
    } catch (e: any) {
      if (e?.response?.status === 409) alert('Valore già presente nella lista.');
      else console.error('Errore aggiunta eccezione:', e);
    } finally {
      setAddingLockEcc(false);
    }
  };

  const handleDipendenteNegozioChange = async (neg: string) => {
    setDipendenteNegozioDropdown(neg);
    setDipendenteDropdown('');
    setDipendenteOptions([]);
    if (!neg) return;
    try {
      setDipendentiLoading(true);
      setDipendenteOptions(await dipendenteService.getByNegozio(neg));
    } catch (e) {
      console.error('Errore caricamento dipendenti per eccezione lock:', e);
    } finally {
      setDipendentiLoading(false);
    }
  };

  const handleDeleteLockEccezione = async (id: number) => {
    try {
      const { coordinatoreLockEccezioniService } = await import('../api/impostazioniService');
      await coordinatoreLockEccezioniService.delete(id);
      setLockEccezioni(prev => prev.filter(e => e.id !== id));
    } catch (e) {
      console.error('Errore eliminazione eccezione:', e);
    }
  };

  // Carica LOG utente (primo/secondo/terzo/quarto) se presente in tabella `log`
  useEffect(() => {
    const loadUserLog = async () => {
      try {
        setLoadingUserLog(true);
        const values = await dashboardService.getUserLog();
        setUserLogValues(values && values.length > 0 ? values : null);
      } catch (e) {
        console.error('Errore nel caricamento LOG utente:', e);
        setUserLogValues(null);
      } finally {
        setLoadingUserLog(false);
      }
    };
    if (user) loadUserLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.username, user?.nome]);

  // Carica la label del mese LOG (per tutti gli utenti)
  useEffect(() => {
    const loadLogMese = async () => {
      try {
        const { impostazioniService } = await import('../api/impostazioniService');
        const mese = await impostazioniService.getLogMeseLabel();
        setLogMeseLabel(mese);
      } catch (e) {
        console.error('Errore nel caricamento mese LOG:', e);
      }
    };
    if (user) loadLogMese();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.username]);

  // Controlla se il Coordinatore ha dipendenti con Differenza < 0 nel mese SUCCESSIVO
  // L'alert è attivo solo dal giorno 16 alla fine del mese corrente
  useEffect(() => {
    if (user?.ruolo !== 'Coordinatore') return;

    const now = new Date();
    // Attivo solo dal 16 del mese corrente in poi
    if (now.getDate() < 16) return;

    const MESI = [
      'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
      'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
    ];

    // Mese CORRENTE
    const meseNome = MESI[now.getMonth()];
    const anno = now.getFullYear();

    const checkOreAlert = async () => {
      try {
        const { apiRequest: apiFetch } = await import('../api/api');

        // 1. Recupera tutti i negozi accessibili per questo coordinatore
        const negozi = await apiFetch<{ neg: string; negozio: string }[]>('/negozi/accessibili');
        if (!negozi || negozi.length === 0) return;

        // 2. Per ogni negozio chiama le statistiche in parallelo
        const results = await Promise.all(
          negozi.map(async (n) => {
            try {
              const data = await apiFetch<any[]>(
                `/dipendenti/statistiche?negozio=${encodeURIComponent(n.neg)}&mese=${meseNome}&anno=${anno}`
              );
              // ESCLUDI SPECIFICI DIPENDENTI (SOBRINO GIORGIA) PERCHÈ NON AGGIORNATA NELL'APP
              const ESCLUSI_ORE_ALERT: { nome: string; cognome: string }[] = [
                { nome: 'sobrino', cognome: 'giorgia' },
              ];
              const sottoContratto = (data || [])
                .filter((d: any) => !ESCLUSI_ORE_ALERT.some(
                  (e) => e.nome === (d.nomeDipendente ?? '').toLowerCase() && e.cognome === (d.cognomeDipendente ?? '').toLowerCase()
                ))
                .filter((d: any) => Math.round((d.attuale ?? 0) * 60) < Math.round((d.oreMensiliContratto ?? 0) * 60))
                .map((d: any) => ({
                  nome: d.nomeDipendente ?? '',
                  cognome: d.cognomeDipendente ?? '',
                  differenza: Math.round(((d.attuale ?? 0) - (d.oreMensiliContratto ?? 0)) * 60) / 60,
                }));
              return { neg: n.neg, negozio: n.negozio, dipendenti: sottoContratto };
            } catch {
              return { neg: n.neg, negozio: n.negozio, dipendenti: [] };
            }
          })
        );

        // 3. Tieni solo i negozi che hanno almeno un dipendente sotto contratto
        const negoziFuoriContratto = results.filter((r) => r.dipendenti.length > 0);

        if (negoziFuoriContratto.length > 0) {
          setOreAlertNegozi(negoziFuoriContratto);
          setOreAlertMese(`${meseNome} ${anno}`);
          setOreAlertOpen(true);
        }
      } catch (e) {
        console.error('Errore nel controllo ore contratto:', e);
      }
    };

    checkOreAlert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.ruolo]);

  // Carica la lista dei coordinatori (solo Admin)
  useEffect(() => {
    if (user?.ruolo !== 'Admin') return;
    const loadCoordinatori = async () => {
      try {
        const { apiRequest: apiFetch } = await import('../api/api');
        const data = await apiFetch<{ username: string; nome: string }[]>('/gestione-utenti/coordinatori');
        setCoordinatoriList(data || []);
      } catch (e) {
        console.error('Errore nel caricamento coordinatori:', e);
      }
    };
    loadCoordinatori();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.ruolo]);

  // Funzione per controllare le ore per Admin (in base al coordinatore selezionato)
  const handleCheckOreAdmin = async () => {
    setAdminControlloDialogOpen(false);
    setAdminOreAlertLoading(true);
    try {
      const { apiRequest: apiFetch } = await import('../api/api');
      const MESI = [
        'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
        'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
      ];
      const meseNome = MESI[adminSelectedMese];
      const anno = adminSelectedAnno;

      let negozi: { neg: string; negozio: string }[] = [];

      if (selectedCoordinatore === '__tutti__') {
        // Recupera tutti i negozi accessibili (per Admin = tutti)
        negozi = await apiFetch<{ neg: string; negozio: string }[]>('/negozi/accessibili');
      } else {
        // Recupera solo i negozi del coordinatore selezionato
        negozi = await apiFetch<{ neg: string; negozio: string }[]>(
          `/negozi/per-coordinatore/${encodeURIComponent(selectedCoordinatore)}`
        );
      }

      if (!negozi || negozi.length === 0) {
        alert('Nessun negozio trovato per la selezione effettuata.');
        return;
      }

      const results = await Promise.all(
        negozi.map(async (n) => {
          try {
            const data = await apiFetch<any[]>(
              `/dipendenti/statistiche?negozio=${encodeURIComponent(n.neg)}&mese=${meseNome}&anno=${anno}`
            );
            const ESCLUSI_ORE_ALERT: { nome: string; cognome: string }[] = [
              { nome: 'giorgia', cognome: 'sobrino' },
            ];
            const sottoContratto = (data || [])
              .filter((d: any) => !ESCLUSI_ORE_ALERT.some(
                (e) => e.nome === (d.nomeDipendente ?? '').toLowerCase() && e.cognome === (d.cognomeDipendente ?? '').toLowerCase()
              ))
              .filter((d: any) => Math.round((d.attuale ?? 0) * 60) < Math.round((d.oreMensiliContratto ?? 0) * 60))
              .map((d: any) => ({
                nome: d.nomeDipendente ?? '',
                cognome: d.cognomeDipendente ?? '',
                differenza: Math.round(((d.attuale ?? 0) - (d.oreMensiliContratto ?? 0)) * 60) / 60,
              }));
            return { neg: n.neg, negozio: n.negozio, dipendenti: sottoContratto };
          } catch {
            return { neg: n.neg, negozio: n.negozio, dipendenti: [] };
          }
        })
      );

      const negoziFuoriContratto = results.filter((r) => r.dipendenti.length > 0);

      if (negoziFuoriContratto.length === 0) {
        alert(`Nessun dipendente con ore sotto contratto per ${meseNome} ${anno}.`);
        return;
      }

      setOreAlertNegozi(negoziFuoriContratto);
      setOreAlertMese(`${meseNome} ${anno}`);
      setOreAlertTriggeredByAdmin(true);
      setOreAlertOpen(true);
    } catch (e) {
      console.error('Errore nel controllo ore admin:', e);
      alert('Errore nel controllo delle ore.');
    } finally {
      setAdminOreAlertLoading(false);
    }
  };
  
  const handleToggleSlider = async (checked: boolean) => {
    if (loadingImpostazioni) return;
    
    setLoadingImpostazioni(true);
    try {
      const { impostazioniService } = await import('../api/impostazioniService');
      await impostazioniService.updateTrascinaConfermareSempre(checked);
      setSliderAlwaysEnabled(checked);
      
      // Aggiorna anche il localStorage per compatibilità
      const raw = localStorage.getItem('appSettings');
      const parsed = raw ? JSON.parse(raw) : {};
      parsed.sliderAlwaysEnabled = checked;
      localStorage.setItem('appSettings', JSON.stringify(parsed));
      
    } catch (error) {
      console.error('Errore nel salvataggio dell\'impostazione:', error);
      alert('Errore nel salvataggio dell\'impostazione');
    } finally {
      setLoadingImpostazioni(false);
    }
  };

  const handleRunOutboxAudit = async () => {
    setAuditLoading(true);
    try {
      const { apiRequest: apiFetch } = await import('../api/api');
      const res = await apiFetch<{ success: boolean; anomalieTotali: number; report: any }>(
        '/admin/outbox-audit/run',
        { method: 'POST' }
      );
      if (res.anomalieTotali === 0) {
        setAuditSnackbar({ open: true, severity: 'success', message: 'Audit completato: nessuna anomalia rilevata.' });
      } else {
        setAuditSnackbar({ open: true, severity: 'warning', message: `Audit completato: ${res.anomalieTotali} anomali${res.anomalieTotali === 1 ? 'a' : 'e'} rilevat${res.anomalieTotali === 1 ? 'a' : 'e'}. Controllare i log del backend.` });
      }
    } catch (err: any) {
      setAuditSnackbar({ open: true, severity: 'error', message: "Errore durante l'audit outbox. Controllare i log." });
    } finally {
      setAuditLoading(false);
    }
  };

  const handleToggleOutbox = async (checked: boolean) => {
    if (loadingImpostazioni) return;
    setLoadingImpostazioni(true);
    try {
      const { impostazioniService } = await import('../api/impostazioniService');
      await impostazioniService.updateOutboxEnabled(checked);
  setOutboxEnabled(checked);
  // Ricalcola stato effettivo con il flag profilo
  setOutboxEffective(outboxProfileFlag && checked);
      // salva anche su localStorage per fallback
      const raw = localStorage.getItem('appSettings');
      const parsed = raw ? JSON.parse(raw) : {};
  parsed.outboxEnabled = checked;
      localStorage.setItem('appSettings', JSON.stringify(parsed));
    } catch (err) {
      console.error('Errore aggiornamento outbox:', err);
      alert('Errore nel salvataggio dell\'impostazione outbox');
    } finally {
      setLoadingImpostazioni(false);
    }
  };

  const handleChangeLogMese = async (nuovoMese: string) => {
    setSavingLogMese(true);
    try {
      const { impostazioniService } = await import('../api/impostazioniService');
      await impostazioniService.updateLogMeseLabel(nuovoMese);
      setLogMeseLabel(nuovoMese);
    } catch (err) {
      console.error('Errore aggiornamento mese LOG:', err);
      alert('Errore nel salvataggio del mese LOG');
    } finally {
      setSavingLogMese(false);
    }
  };

  const isCoordinatoreOrAdmin = user?.ruolo === 'Coordinatore' || user?.ruolo === 'Admin';
  // Include Dipendente, Linker, Maker, Referente - i permessi sono determinati da AbilitaInsOrari
  const isLimitedRole = user?.ruolo === 'Dipendente' || user?.ruolo === 'Linker' || user?.ruolo === 'Maker' || user?.ruolo === 'Referente';
  const isLimitedRoleWithAbilita = isLimitedRole && !!user?.abilitaInsOrari;

  // Costruiamo le card base (escludendo temporaneamente Gestione Negozi per spostarla alla fine)
  const storeCard: DashboardCard = {
    title: 'Gestione Negozi',
    description: 'Visualizza e gestisci i negozi',
    icon: <Store fontSize="large" />,
    path: '/negozi',
    available: isCoordinatoreOrAdmin,
    color: '#4CAF50'
  };

  const baseCards: DashboardCard[] = [
    {
      title: 'Il Mio Orario',
      description: 'Visualizza e compila i tuoi orari',
      icon: <Schedule fontSize="large" />,
      path: '/mio-orario',
      available: true,
      color: '#2196F3'
    },
    {
      title: 'Scarica Orari Negozio',
      description: 'Scarica il calendario orari del tuo negozio',
      icon: <Download fontSize="large" />,
      onClick: () => setOpenMonthPicker(true),
      // Disponibile per ruoli limitati (Dipendente, Linker, Maker, Referente) SENZA abilitazione
      available: isLimitedRole && !user?.abilitaInsOrari,
      color: '#8E24AA'
    },
    {
      title: 'Orario Dipendente',
      description: 'Visualizza orari di un dipendente',
      icon: (
        <Box sx={{ position: 'relative', width: 48, height: 48 }}>
          <Person sx={{ fontSize: 48 }} />
          <AccessTime sx={{ position: 'absolute', right: -2, bottom: -2, fontSize: 18, opacity: 0.9, color: 'black' }} />
        </Box>
      ),
      path: '/orario-dipendente',
      available: isCoordinatoreOrAdmin,
      color: '#3f51b5'
    },
    {
      title: 'Gestione Dipendenti',
      description: 'Gestisci i dipendenti',
      icon: <PeopleAlt fontSize="large" />,
      path: '/dipendenti',
      available: isCoordinatoreOrAdmin || user?.ruolo === 'ResponsabileOrari' || isLimitedRoleWithAbilita,
      color: '#FF9800'
    },
    {
      title: 'Generazione Orari',
      description: 'Genera e gestisci gli orari dei dipendenti',
      icon: <CalendarMonth fontSize="large" />,
      path: '/generazione-orario',
  available: isCoordinatoreOrAdmin || user?.ruolo === 'ResponsabileOrari' || (isLimitedRole && !!user?.abilitaInsOrari),
      color: '#607D8B'
    },
    {
      title: 'Gestione Turni',
      description: 'Configura gli orari dei turni',
      icon: <ManageHistory fontSize="large" />,
      path: '/turni',
      available: isCoordinatoreOrAdmin || isLimitedRoleWithAbilita,
      color: '#F44336'
    },
    {
      title: 'Gestione Quantità Personale',
      description: 'Configura il fabbisogno di personale',
      icon: <GroupAdd fontSize="large" />,
      path: '/quantita-personale',
      available: isCoordinatoreOrAdmin || user?.ruolo === 'ResponsabileOrari' || isLimitedRoleWithAbilita,
      color: '#b052c0'
    },
    {
      title: 'Gestione Cambi Turno',
      description: 'Approva o rifiuta le richieste di cambio turno',
      icon: <ChangeCircle fontSize="large" />,
      path: '/gestione-cambi-turno',
      available: isCoordinatoreOrAdmin || user?.ruolo === 'ResponsabileOrari' || isLimitedRoleWithAbilita,
      color: '#FF5722'
    },
    {
      title: 'Controllo Orari',
      description: 'Inserimento massivo assenze e verifica blocchi',
      icon: (
        <Box sx={{ position: 'relative', width: 48, height: 48 }}>
          <Schedule sx={{ fontSize: 48 }} />
          <AccessTime sx={{ position: 'absolute', right: -2, bottom: -2, fontSize: 18, opacity: 0.9, color: 'black' }} />
        </Box>
      ),
      path: '/controllo-orari',
      available: user?.ruolo === 'Admin',
      color: '#00897B'
    }
  ];

  // Card logo per Coordinatore (solo logo, nessun colore, link esterno nuova scheda)
  const logoCard: DashboardCard = {
    title: 'Logo', // interno, non mostrato
    description: '',
    icon: <img src="/logo192.png" alt="Logo" style={{ height: 80, width: 'auto' }} />,
    externalUrl: 'https://gioiellidivalenza.com',
    available: user?.ruolo === 'Coordinatore',
    color: 'transparent',
    plain: true
  };

  // Inserisci logo card dopo "Il Mio Orario" se disponibile
  const menuItems: DashboardCard[] = [...baseCards];
  if (logoCard.available) {
    menuItems.splice(1, 0, logoCard);
  }
  // Aggiungi Gestione Negozi come ultima
  menuItems.push(storeCard);

  if (!user) {
    navigate('/login');
    return null;
  }

  const handleLogout = () => {
    authService.logout();
  };

  const handleMonthYearConfirm = async (monthIndex: number, year: number) => {
    setOpenMonthPicker(false);
    if (!user?.negozio) {
      setOrariNonPubblicatiMsg('Nessun negozio associato al tuo utente.');
      return;
    }

    try {
      const mesi = [
        'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
        'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
      ];
      const monthName = mesi[monthIndex];
      
      const status = await visibilitaService.getStatus(user.negozio, monthName, year);
      
      if (status.visibile) {
        setDownloadParams({
          negozio: user.negozio,
          mese: monthIndex + 1,
          anno: year
        });
      } else {
        setOrariNonPubblicatiMsg('Gli orari non sono ancora stati pubblicati per il mese selezionato');
      }
    } catch (error) {
      console.error('Errore verifica visibilità:', error);
      setOrariNonPubblicatiMsg('Errore nel recupero degli orari. Verifica di avere un negozio associato al tuo utente.');
    }
  };
  // fine impostazioni

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            App Orari - Benvenuto {user.nome} ({user.ruolo})
          </Typography>
          <IconButton color="inherit" onClick={handleLogout}>
            <ExitToApp />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container 
        maxWidth="lg" 
        sx={{ 
          mt: 4, 
          mb: 4, 
          minHeight: 'calc(100vh - 200px)', // Altezza minima per permettere il centramento verticale
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}
      >
        <Typography variant="h4" gutterBottom align="center">
          
        </Typography>
        
        <Box 
          sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 3, 
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px' // Altezza minima per il centramento verticale dei riquadri
          }}
        >
          {menuItems
            .filter(item => item.available)
            .map((item, index) => (
              <Card 
                key={index}
                sx={{ 
                  width: 300,
                  height: 200,
                  display: 'flex', 
                  flexDirection: 'column',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: 3,
                  borderRadius: 3,
                  position: 'relative',
                  overflow: 'hidden',
                  ...(item.plain ? {
                    '&:hover': { boxShadow: '0 12px 24px rgba(0,0,0,0.18)' }
                  } : {
                    '&:hover': {
                      transform: 'scale(1.05) translateY(-8px)',
                      boxShadow: '0 16px 32px rgba(0,0,0,0.2)',
                      '& .card-icon': {
                        transform: 'scale(1.2)',
                        color: item.color
                      },
                      '&::before': {
                        opacity: 1
                      }
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: `linear-gradient(135deg, ${item.color}15, ${item.color}05)`,
                      opacity: 0,
                      transition: 'opacity 0.3s ease'
                    }
                  })
                }}
                onClick={() => {
                  if (item.onClick) {
                    item.onClick();
                  } else if (item.externalUrl) {
                    window.open(item.externalUrl, '_blank', 'noopener');
                  } else if (item.path) {
                    navigate(item.path);
                  }
                }}
              >
                <CardContent sx={{ 
                  flexGrow: 1, 
                  textAlign: 'center',
                  position: 'relative',
                  zIndex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  py: 4
                }}>
                  <Box 
                    className="card-icon"
                    sx={{ 
                      mb: 2, 
                      color: item.plain ? 'inherit' : item.color,
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {item.icon}
                  </Box>
                  {!item.plain && (
                    <>
                      <Typography 
                        gutterBottom 
                        variant="h5" 
                        component="div"
                        sx={{ 
                          fontWeight: 600,
                          mb: 1,
                          color: 'text.primary'
                        }}
                      >
                        {item.title}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          fontSize: '0.9rem',
                          lineHeight: 1.4
                        }}
                      >
                        {item.description}
                      </Typography>
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
        </Box>

        <Box sx={{ mt: 4, display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, minWidth: 280 }}>
            <Typography variant="h6" gutterBottom>
              Informazioni Utente
            </Typography>
            <Typography variant="body1">
              <strong>Nome:</strong> {user.nome}
            </Typography>
            <Typography variant="body1">
              <strong>Ruolo:</strong> {user.ruolo}
            </Typography>
            <Typography variant="body1">
              <strong>Negozio:</strong> {user.negozio || '-'}
            </Typography>
          </Box>

          {(user.ruolo === 'Admin' || userLogValues) && (
            <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, minWidth: 280, mt: 1 }}>
              {user.ruolo === 'Admin' ? (
                <>
                  <FormControl size="small" sx={{ mb: 1, minWidth: 120
                   }}>
                    <InputLabel id="log-mese-label">Mese LOG</InputLabel>
                    <Select
                      labelId="log-mese-label"
                      label="Mese LOG"
                      value={logMeseLabel}
                      disabled={savingLogMese}
                      onChange={(e) => handleChangeLogMese(e.target.value)}
                    >
                      {['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno',
                        'Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'].map(m => (
                        <MenuItem key={m} value={m}>{m}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {savingLogMese && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Salvataggio…
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="h6" gutterBottom translate="no">
                  LOG {logMeseLabel}
                </Typography>
              )}
              {loadingUserLog ? (
                <Typography variant="body2" color="text.secondary">Caricamento…</Typography>
              ) : userLogValues ? (
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {userLogValues.filter(v => (v ?? '').toString().trim() !== '').join(' | ')}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nessun dato LOG per questo utente.
                </Typography>
              )}
            </Box>
          )}

          {/* Pannello "Controllo Ore Coordinatori" solo per Admin — affiancato ai LOG */}
          {user.ruolo === 'Admin' && (
            <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, minWidth: 200, maxWidth: 260, mt: 0.5, display: 'flex', alignItems: 'center' }}>
              <Button
                variant="contained"
                color="warning"
                size="small"
                fullWidth
                startIcon={<WarningAmberIcon />}
                onClick={() => setAdminControlloDialogOpen(true)}
                disabled={adminOreAlertLoading}
              >
                {adminOreAlertLoading ? 'Verifica…' : 'Controllo ore'}
              </Button>
            </Box>
          )}

          {/* Dialog selezione coordinatore + mese per "Controllo Ore" Admin */}
          <Dialog open={adminControlloDialogOpen} onClose={() => setAdminControlloDialogOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle>Controllo Ore Coordinatori</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <FormControl size="small" fullWidth>
                <InputLabel id="admin-coord-label">Coordinatore</InputLabel>
                <Select
                  labelId="admin-coord-label"
                  label="Coordinatore"
                  value={selectedCoordinatore}
                  onChange={(e) => setSelectedCoordinatore(e.target.value)}
                >
                  <MenuItem value="__tutti__">— Tutti —</MenuItem>
                  {coordinatoriList.map((c) => (
                    <MenuItem key={c.username} value={c.username}>
                      {c.nome || c.username}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel id="admin-mese-label">Mese</InputLabel>
                <Select
                  labelId="admin-mese-label"
                  label="Mese"
                  value={adminSelectedMese}
                  onChange={(e) => setAdminSelectedMese(Number(e.target.value))}
                >
                  {['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'].map((m, i) => (
                    <MenuItem key={i} value={i}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel id="admin-anno-label">Anno</InputLabel>
                <Select
                  labelId="admin-anno-label"
                  label="Anno"
                  value={adminSelectedAnno}
                  onChange={(e) => setAdminSelectedAnno(Number(e.target.value))}
                >
                  {[new Date().getFullYear() - 1, new Date().getFullYear()].map((y) => (
                    <MenuItem key={y} value={y}>{y}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAdminControlloDialogOpen(false)}>Annulla</Button>
              <Button variant="contained" color="warning" startIcon={<WarningAmberIcon />} onClick={handleCheckOreAdmin}>
                Controlla
              </Button>
            </DialogActions>
          </Dialog>
        </Box>

        {user.ruolo === 'Admin' && (
          <Paper elevation={2} sx={{ mt: 4, p: 2 }}>
            <UtentiAttivi />
          </Paper>
        )}

        {user.ruolo === 'Admin' && (
          <Paper elevation={2} sx={{ mt: 4, p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Impostazioni
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={sliderAlwaysEnabled}
                  onChange={(e) => handleToggleSlider(e.target.checked)}
                  disabled={loadingImpostazioni}
                />
              }
              label="Abilita la compilazione anticipata degli orari"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Quando selezionato, i dipendenti possono inserire gli orari anticipatamente.
            </Typography>

            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={outboxEnabled && outboxProfileFlag}
                    onChange={(e) => handleToggleOutbox(e.target.checked)}
                    disabled={loadingImpostazioni || !outboxProfileFlag}
                  />
                }
                  label={outboxProfileFlag ? "Abilita invio Outbox (POST esterne)" : "Outbox forzatamente disabilitato (profilo)"}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {outboxProfileFlag
                    ? (outboxEnabled ? "Attualmente gli eventi verranno inviati." : "Disabilitato: nessun invio verso sistemi esterni.")
                    : "Il profilo di esecuzione ha disabilitato l'invio, indipendentemente dal toggle."}
              </Typography>
                {outboxProfileFlag && (
                  <Typography variant="caption" color="text.secondary">
                    Stato effettivo runtime: {outboxEffective ? 'ABILITATO' : 'DISABILITATO'}
                  </Typography>
                )}
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleRunOutboxAudit}
                  disabled={auditLoading}
                  startIcon={auditLoading ? <CircularProgress size={16} /> : undefined}
                >
                  {auditLoading ? 'Audit in corso…' : 'Audit riconciliazione Outbox'}
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Verifica che ogni turno abbia il corrispondente evento in gdv_outbox_event. L'audit gira automaticamente ogni 5 minuti.
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Eccezioni Lock Coordinatore */}
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
              Eccezioni Lock Coordinatore
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Negozi o coordinatori per cui il lock automatico non si attiva (dal {new Date().getDate() <= 23 ? 'ultimo giorno del mese - 7' : 'giorno corrente'}).
            </Typography>

            {lockEccezioniLoading ? (
              <CircularProgress size={20} />
            ) : (
              <>
                {/* Negozi esclusi */}
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Negozi esclusi</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1, minHeight: 32 }}>
                  {lockEccezioni.filter(e => e.tipo === 'NEGOZIO').map((e: any) => (
                    <Chip
                      key={e.id}
                      label={e.valore}
                      size="small"
                      onDelete={() => handleDeleteLockEccezione(e.id)}
                      color="warning"
                      variant="outlined"
                    />
                  ))}
                  {lockEccezioni.filter(e => e.tipo === 'NEGOZIO').length === 0 && (
                    <Typography variant="caption" color="text.secondary">Nessuno</Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <FormControl size="small" sx={{ width: 220 }} disabled={addingLockEcc}>
                    <InputLabel id="neg-escl-label">Negozio</InputLabel>
                    <Select
                      labelId="neg-escl-label"
                      label="Negozio"
                      value={negozioDropdown}
                      onChange={e => setNegozioDropdown(e.target.value)}
                    >
                      <MenuItem value=""><em>Seleziona…</em></MenuItem>
                      {negozioOptions
                        .filter(n => !lockEccezioni.some(e => e.tipo === 'NEGOZIO' && e.valore === n.neg))
                        .map(n => (
                          <MenuItem key={n.neg} value={n.neg}>{n.neg} — {n.negozio}</MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleAddLockEccezione('NEGOZIO', negozioDropdown)}
                    disabled={addingLockEcc || !negozioDropdown}
                  >
                    Aggiungi
                  </Button>
                </Box>

                {/* Dipendenti esclusi */}
                <Typography variant="body2" sx={{ fontWeight: 600, mt: 2, mb: 0.5 }}>Dipendenti esclusi</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1, minHeight: 32 }}>
                  {lockEccezioni.filter(e => e.tipo === 'DIPENDENTE').map((e: any) => (
                    <Chip
                      key={e.id}
                      label={e.note || e.valore}
                      size="small"
                      onDelete={() => handleDeleteLockEccezione(e.id)}
                      color="warning"
                      variant="outlined"
                    />
                  ))}
                  {lockEccezioni.filter(e => e.tipo === 'DIPENDENTE').length === 0 && (
                    <Typography variant="caption" color="text.secondary">Nessuno</Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <FormControl size="small" sx={{ width: 260 }} disabled={addingLockEcc}>
                    <InputLabel id="dipendente-neg-escl-label">Negozio</InputLabel>
                    <Select
                      labelId="dipendente-neg-escl-label"
                      label="Negozio"
                      value={dipendenteNegozioDropdown}
                      onChange={e => handleDipendenteNegozioChange(e.target.value)}
                    >
                      <MenuItem value=""><em>Seleziona…</em></MenuItem>
                      {negozioOptions.map(n => (
                        <MenuItem key={n.neg} value={n.neg}>{n.neg} — {n.negozio}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ width: 300 }} disabled={addingLockEcc || !dipendenteNegozioDropdown || dipendentiLoading}>
                    <InputLabel id="dipendente-escl-label">Dipendente</InputLabel>
                    <Select
                      labelId="dipendente-escl-label"
                      label="Dipendente"
                      value={dipendenteDropdown}
                      onChange={e => setDipendenteDropdown(e.target.value)}
                    >
                      <MenuItem value=""><em>{dipendentiLoading ? 'Caricamento…' : 'Seleziona…'}</em></MenuItem>
                      {dipendenteOptions
                        .filter(d => !lockEccezioni.some(e => e.tipo === 'DIPENDENTE' && e.valore === d.codicePersonale))
                        .map(d => (
                          <MenuItem key={d.codicePersonale} value={d.codicePersonale}>
                            {d.codicePersonale} — {d.nome}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleAddLockEccezione('DIPENDENTE', dipendenteDropdown)}
                    disabled={addingLockEcc || !dipendenteNegozioDropdown || !dipendenteDropdown}
                  >
                    Aggiungi
                  </Button>
                </Box>

                {/* Coordinatori esclusi */}
                <Typography variant="body2" sx={{ fontWeight: 600, mt: 2, mb: 0.5 }}>Coordinatori esclusi</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1, minHeight: 32 }}>
                  {lockEccezioni.filter(e => e.tipo === 'COORDINATORE').map((e: any) => (
                    <Chip
                      key={e.id}
                      label={e.valore}
                      size="small"
                      onDelete={() => handleDeleteLockEccezione(e.id)}
                      color="info"
                      variant="outlined"
                    />
                  ))}
                  {lockEccezioni.filter(e => e.tipo === 'COORDINATORE').length === 0 && (
                    <Typography variant="caption" color="text.secondary">Nessuno</Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <FormControl size="small" sx={{ width: 260 }} disabled={addingLockEcc}>
                    <InputLabel id="coord-escl-label">Coordinatore</InputLabel>
                    <Select
                      labelId="coord-escl-label"
                      label="Coordinatore"
                      value={coordDropdown}
                      onChange={e => setCoordDropdown(e.target.value)}
                    >
                      <MenuItem value=""><em>Seleziona…</em></MenuItem>
                      {coordinatoriList
                        .filter(c => !lockEccezioni.some(e => e.tipo === 'COORDINATORE' && e.valore === c.username))
                        .map(c => (
                          <MenuItem key={c.username} value={c.username}>{c.nome || c.username}</MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleAddLockEccezione('COORDINATORE', coordDropdown)}
                    disabled={addingLockEcc || !coordDropdown}
                  >
                    Aggiungi
                  </Button>
                </Box>
              </>
            )}
          </Paper>
        )}
      </Container>

      {downloadParams && (
        <HiddenScheduleRenderer
          negozio={downloadParams.negozio}
          mese={downloadParams.mese}
          anno={downloadParams.anno}
          onComplete={() => {
            setDownloadParams(null);
          }}
          onError={(msg) => {
            setDownloadParams(null);
            alert(msg);
          }}
        />
      )}
      
      <MonthYearPicker
        open={openMonthPicker}
        onClose={() => setOpenMonthPicker(false)}
        onConfirm={handleMonthYearConfirm}
      />

      <Snackbar
        open={auditSnackbar.open}
        autoHideDuration={6000}
        onClose={() => setAuditSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={auditSnackbar.severity} onClose={() => setAuditSnackbar(s => ({ ...s, open: false }))}>
          {auditSnackbar.message}
        </Alert>
      </Snackbar>

      {/* Alert Differenza Ore < 0 per Coordinatore / Admin */}
      <Dialog
        open={oreAlertOpen}
        onClose={oreAlertTriggeredByAdmin ? () => setOreAlertOpen(false) : () => {}}
        disableEscapeKeyDown={!oreAlertTriggeredByAdmin}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderTop: '6px solid #f44336',
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <WarningAmberIcon sx={{ color: '#f44336', fontSize: 32 }} />
          <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
            Attenzione: Ore non coperte — {oreAlertMese}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" gutterBottom>
            I seguenti dipendenti hanno le <strong>Ore Assegnate</strong> inferiori alle{' '}
            <strong>Ore Contratto</strong> (Differenza &lt; 0). È necessario intervenire
            nella <strong>Generazione Orari</strong> per coprire le ore mancanti:
          </Typography>

          {oreAlertNegozi.map((negozioGroup) => (
            <Box key={negozioGroup.neg} sx={{ mb: 2 }}>
              {/* Intestazione negozio (solo se ci sono più negozi) */}
              {oreAlertNegozi.length > 1 && (
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    mt: 1,
                    mb: 0.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    pb: 0.5,
                  }}
                >
                  Negozio: {negozioGroup.negozio || negozioGroup.neg}
                </Typography>
              )}
              <List dense disablePadding>
                {negozioGroup.dipendenti.map((d, i) => (
                  <ListItem key={i} sx={{ py: 0.5 }}>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {d.nome} {d.cognome}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" sx={{ color: '#f44336' }}>
                          Differenza: {(() => { const tot = Math.round(d.differenza * 60); const sign = tot < 0 ? '-' : ''; const absTot = Math.abs(tot); const h = Math.floor(absTot / 60); const m = absTot % 60; return `${sign}${h}h ${m.toString().padStart(2, '0')}m`; })()}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
              {/* Bottone "Vai" specifico per questo negozio */}
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<CalendarMonth />}
                onClick={() => {
                  setOreAlertOpen(false);
                  // Naviga al mese CORRENTE (stesso calcolo del useEffect)
                  const now = new Date();
                  const mese = now.getMonth() + 1;
                  const anno = now.getFullYear();
                  navigate(`/generazione-orario?neg=${encodeURIComponent(negozioGroup.neg)}&mese=${mese}&anno=${anno}`);
                }}
                sx={{ mt: 0.5, mb: 0.5 }}
              >
                Vai a Generazione Orari{oreAlertNegozi.length > 1 ? ` — ${negozioGroup.negozio || negozioGroup.neg}` : ''}
              </Button>
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            color="error"
            onClick={() => setOreAlertOpen(false)}
            sx={{ minWidth: 130 }}
          >
            OK, ho capito
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar per messaggio orari non pubblicati */}
      <Snackbar
        open={!!orariNonPubblicatiMsg}
        autoHideDuration={6000}
        onClose={() => setOrariNonPubblicatiMsg(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setOrariNonPubblicatiMsg(null)} 
          severity="warning" 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {orariNonPubblicatiMsg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Dashboard;
